import {
  SHOWCASE_LANGUAGE_KEY,
  showcaseLanguageNames,
} from "src/shared/util/src/language/showcase";
import { parseAppLanguage } from "src/shared/util/src/language/dico";
import { useShowcaseLanguage } from "./useShowcaseLanguage";
import {
  createElement,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { MacIcon } from "src/shared/ui/src/design/MacIcon";
import {
  designActionStyle,
  type DesignActionTone,
} from "src/shared/ui/src/design/designActionStyle";
import { designTokens, interfaceFont } from "src/shared/ui/src/design/tokens";
import { Link } from "react-router-dom";
import "./home.css";
import PlanningStory from "./PlanningStory";
import CompetitionStory from "./CompetitionStory";

type Theme = "light" | "dark";
type Shot = "competition" | "planning" | "editor" | "discipline" | "group";
const assets = "/showcase/";

const shotExtensions: Record<Shot, string> = {
  group: "jpg",
  competition: "png",
  planning: "jpg",
  editor: "jpg",
  discipline: "jpg",
};
const shotDimensions: Record<Shot, { width: number; height: number }> = {
  group: { width: 1700, height: 1034 },
  competition: { width: 1700, height: 1034 },
  planning: { width: 1512, height: 900 },
  editor: { width: 1512, height: 900 },
  discipline: { width: 1512, height: 900 },
};

function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem("meet-manager.showcase-theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    /* La préférence reste utilisable pour cette session. */
  }
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function WidgetStory({ theme }: { theme: Theme }) {
  const { language, translations } = useShowcaseLanguage();
  useEffect(() => {
    if (document.querySelector("script[data-showcase-story]")) return;
    const script = document.createElement("script");
    script.src = assets + "widget-story.js";
    script.dataset.showcaseStory = "true";
    document.head.append(script);
  }, []);
  return createElement("widget-story", {
    key: language,
    "data-theme": theme,
    "data-language": language,
    "data-translations": JSON.stringify(translations),
  });
}
export default function HomePage() {
  const { language, setLanguage, t, assetBase } = useShowcaseLanguage();
  const shotTitles: Record<Shot, string> = {
    group: t("Feuille de match, essais et classement des athlètes"),
    competition: t("Organisation des groupes et suivi de la compétition"),
    planning: t("Répartition des groupes et horaires par plateau"),
    editor: t("Composition des affichages et des overlays"),
    discipline: t("Paramètres de la discipline et du planning"),
  };
  const steps = [
    ["discipline", t("Configurer la discipline")],
    ["affichages", t("Créer les affichages")],
    ["organisation", t("Inscriptions et planning")],
    ["competition", t("Gérer et suivre la compétition")],
    ["diffusion", t("Diffuser en direct")],
  ];
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t("Meet Manager - Gestion de compétition");
    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute(
      "content",
      t(
        "Gérez vos compétitions, de leur préparation à la diffusion des résultats. Centralisez les inscriptions, le planning, l’arbitrage et bien plus encore.",
      ),
    );
  }, [language]);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const update = (event: StorageEvent) => {
      if (event.key !== SHOWCASE_LANGUAGE_KEY) return;
      const next = parseAppLanguage(event.newValue);
      if (next) setLanguage(next);
    };
    window.addEventListener("storage", update);
    return () => window.removeEventListener("storage", update);
  }, [setLanguage]);
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [hero, setHero] = useState<Shot>("group");
  const [menuOpen, setMenuOpen] = useState(false);
  const [enlarged, setEnlarged] = useState<Shot | null>(null);
  const [currentStep, setCurrentStep] = useState(-1);
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const source = (shot: Shot) =>
    `${assetBase}${shot}-${theme}.${shotExtensions[shot]}`;
  const actionStyle = (
    tone: DesignActionTone = "accent",
    hasLabel = true,
  ): CSSProperties => ({
    ...designActionStyle(theme, tone, hasLabel),
    display: undefined,
    ...(hasLabel
      ? { background: "#00c49e", border: "1px solid #00c49e", color: "#00291f" }
      : {}),
    ...(!hasLabel ? { padding: 5 } : {}),
  });
  useEffect(() => {
    const query = matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      try {
        if (localStorage.getItem("meet-manager.showcase-theme")) return;
      } catch {
        /* Préférence système. */
      }
      setTheme(query.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    let frame = 0;
    const clamp = (value: number) => Math.max(0, Math.min(1, value));
    const update = () => {
      frame = 0;
      const readingLine = window.innerHeight * 0.45;
      const stops = [
        ...container.querySelectorAll<HTMLElement>(".journey-stop"),
      ];
      const geometry = stops.map((element) => ({
        rect: element.getBoundingClientRect(),
        center: parseFloat(
          getComputedStyle(element).getPropertyValue("--journey-marker-center"),
        ),
      }));
      let active = -1;
      const links = container.querySelectorAll<HTMLElement>(".journey-nav a");
      geometry.forEach(({ rect, center }, index) => {
        if (readingLine >= rect.top + center) active = index;
        const start = index === 0 ? center : 0;
        const end = index === stops.length - 1 ? center : rect.height;
        const progress = clamp(
          (readingLine - rect.top - start) / (end - start),
        );
        stops[index].style.setProperty(
          "--journey-progress",
          `${progress * 100}%`,
        );
        const next = geometry[index + 1];
        const linkProgress = next
          ? clamp(
              (readingLine - rect.top - center) /
                (next.rect.top + next.center - rect.top - center),
            )
          : 0;
        links[index]?.style.setProperty(
          "--journey-progress",
          `${linkProgress * 100}%`,
        );
      });
      setCurrentStep(active);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);
  useEffect(() => {
    if (enlarged && !dialog.current?.open) dialog.current?.showModal();
  }, [enlarged]);
  const enlarge = (shot: Shot) => {
    opener.current = document.activeElement as HTMLElement;
    setEnlarged(shot);
  };
  useEffect(() => {
    const element = dialog.current;
    const onClose = () => {
      setEnlarged(null);
      opener.current?.focus();
    };
    element?.addEventListener("close", onClose);
    return () => element?.removeEventListener("close", onClose);
  }, []);
  const close = () => dialog.current?.close();
  const capture = (shot: Shot, priority = false) => (
    <button
      className="capture"
      onClick={() => enlarge(shot)}
      aria-label={`${t("Agrandir")} : ${shotTitles[shot]}`}
    >
      <img
        src={source(shot)}
        alt={shotTitles[shot]}
        loading={priority ? "eager" : "lazy"}
        width={shotDimensions[shot].width}
        height={shotDimensions[shot].height}
      />
    </button>
  );
  const screenshot = (shot: Shot) => (
    <figure className="showcase-shot" aria-label={shotTitles[shot]}>
      {capture(shot)}
    </figure>
  );
  const section = (
    index: number,
    title: string,
    description: string,
    items: string[],
    shot: Shot,
  ) => (
    <section
      id={steps[index][0]}
      className={`wrap journey-section journey-stop ${currentStep >= index ? "is-current" : ""}`}
      data-step={`0${index + 1}`}
    >
      {shot === "planning" ? (
        <PlanningStory
          key={`${language}-${theme}`}
          theme={theme}
          title={title}
          description={description}
        />
      ) : (
        <>
          <div className="journey-copy">
            <h2>{title}</h2>
            <p>{description}</p>
            <ul>
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          {shot === "group" ? (
            <CompetitionStory key={`${language}-${theme}`} theme={theme} />
          ) : (
            screenshot(shot)
          )}
        </>
      )}
    </section>
  );
  return (
    <div
      className="showcase"
      data-theme={theme}
      ref={root}
      style={
        {
          "--interface-font": interfaceFont,
          "--control-accent": designTokens[theme].accent,
          "--control-text": designTokens[theme].text,
          "--control-surface": designTokens[theme].surface,
          "--control-border": designTokens[theme].border,
          "--control-background": designTokens[theme].background,
          "--control-chrome": designTokens[theme].chrome,
          "--control-muted": designTokens[theme].muted,
          "--control-selected": designTokens[theme].selected,
          "--control-success": designTokens[theme].success,
          "--control-on-accent": designTokens[theme].onAccent,
        } as CSSProperties
      }
    >
      <a className="skip-link" href="#contenu">
        {t("Aller au contenu")}
      </a>
      <div className="public-header">
        <header className="wrap site-header">
          <Link
            className="brand"
            to="/"
            aria-label={t("Meet Manager, accueil")}
          >
            <img src={assets + "icon.png"} alt="" width="32" height="32" />
            Meet Manager
          </Link>
          <nav
            id="showcase-navigation"
            className={`site-nav ${menuOpen ? "open" : ""}`}
            aria-label={t("Navigation principale")}
            onClick={() => setMenuOpen(false)}
          >
            <a href="#contenu" aria-current="page">
              {t("Accueil")}
            </a>
            <Link to="/app">{t("Application web")}</Link>
            <Link to="/contact">{t("Contact")}</Link>
            <Link to="/licensed">{t("Mon espace")}</Link>
          </nav>
          {import.meta.env.DEV && (
            <select
              className="language-select"
              aria-label={t("Langue")}
              value={language}
              onChange={(event) => {
                const next = parseAppLanguage(event.target.value);
                if (!next) return;
                setLanguage(next);
                try {
                  localStorage.setItem(SHOWCASE_LANGUAGE_KEY, next);
                } catch {
                  /* Session preference. */
                }
              }}
            >
              {Object.entries(showcaseLanguageNames).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          )}
          <button
            className="theme-toggle"
            style={actionStyle("neutral", false)}
            aria-label={
              theme === "light"
                ? t("Activer le mode sombre")
                : t("Activer le mode clair")
            }
            title={theme === "light" ? t("Mode sombre") : t("Mode clair")}
            onClick={() => {
              const next = theme === "light" ? "dark" : "light";
              setTheme(next);
              try {
                localStorage.setItem("meet-manager.showcase-theme", next);
              } catch {
                /* Choix de session. */
              }
            }}
          >
            <MacIcon name={theme === "light" ? "moon" : "sun"} size={15} />
          </button>
          <Link
            style={actionStyle()}
            className="btn primary header-demo"
            to="/contact"
          >
            {t("Demander une démo")}
            <MacIcon name="export" size={14} />
          </Link>
          <button
            className="menu-toggle"
            style={actionStyle("neutral", false)}
            aria-label={menuOpen ? t("Fermer le menu") : t("Ouvrir le menu")}
            aria-controls="showcase-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <MacIcon name={menuOpen ? "close" : "grid"} size={16} />
          </button>
        </header>
      </div>
      <main id="contenu">
        <section className="wrap hero">
          <div>
            <p className="brand hero-brand">Meet Manager</p>
            <h1>
              {t("Solution de gestion")}
              <br />
              <em>{t("de compétition")}</em>
            </h1>
            <p className="lede">
              {t(
                "Gérez vos compétitions, de leur préparation à la diffusion des résultats. Centralisez les inscriptions, le planning, l’arbitrage et bien plus encore.",
              )}
            </p>
            <p className="hero-detail">
              {t(
                "Configurez les disciplines, les règles et les affichages selon votre sport et votre format de compétition, avec notre accompagnement.",
              )}
            </p>
            <div className="hero-actions">
              <Link style={actionStyle()} className="btn primary" to="/contact">
                {t("Demander une démo")}
                <MacIcon name="arrow" size={14} />
              </Link>
              <Link style={actionStyle("neutral")} className="btn" to="/app">
                {t("Application web")}
                <MacIcon name="export" size={14} />
              </Link>
            </div>
            <p className="quiet hero-note">
              {t("Pour les ligues, les clubs et les organisateurs.")}
            </p>
          </div>
          <figure
            className="showcase-shot hero-product"
            aria-label={shotTitles[hero]}
          >
            {capture(hero, true)}
            <div
              className="product-tabs"
              role="group"
              aria-label={t("Aperçus de l’application")}
            >
              {(["group", "competition", "planning"] as Shot[]).map(
                (shot, i) => (
                  <button
                    key={shot}
                    aria-pressed={hero === shot}
                    onClick={() => setHero(shot)}
                  >
                    {[t("Groupe"), t("Compétition"), t("Planning")][i]}
                  </button>
                ),
              )}
            </div>
          </figure>
        </section>
        <p className="wrap quiet screenshot-note">
          {t(
            "Les captures et l’animation illustrent une configuration de force athlétique avec des données de démonstration.",
          )}
        </p>
        <nav
          className="wrap journey-nav"
          aria-label={t("Les étapes de votre compétition")}
        >
          {steps.map(([id, label], i) => (
            <a
              key={id}
              href={`#${id}`}
              aria-current={currentStep === i ? "step" : undefined}
              className={i < currentStep ? "is-complete" : ""}
            >
              <span>0{i + 1}</span>
              {label}
            </a>
          ))}
        </nav>
        {section(
          0,
          t("Configurez votre discipline avec nous"),
          t(
            "Nous vous accompagnons pour adapter Meet Manager à votre format de compétition, à partir d’une discipline existante ou d’une configuration personnalisée.",
          ),
          [
            t("Définissez les champs et les informations à suivre."),
            t("Configurez les règles et les comportements de la discipline."),
            t("Vérifiez ensemble le déroulement avant la compétition."),
          ],
          "discipline",
        )}
        <section className="broadcast-section" id="affichages">
          <div
            className={`wrap journey-stop ${currentStep >= 1 ? "is-current" : ""}`}
            data-step="02"
          >
            <div className="section-heading">
              <h2>{t("Personnalisez vos affichages")}</h2>
              <p>
                {t(
                  "Modifiez la disposition, les polices et les couleurs. Choisissez les données à afficher et les réactions aux événements de la compétition.",
                )}
              </p>
            </div>
            <WidgetStory theme={theme} />
            <div className="custom-callout">
              <p>
                {t(
                  "Dans cette démonstration, l’événement « Barre réussie » déclenche une animation et un changement de couleur. Les événements dépendent de la discipline configurée.",
                )}
              </p>
              <button
                style={actionStyle("neutral")}
                className="btn"
                onClick={() => enlarge("editor")}
              >
                {t("Voir l’éditeur complet")}
                <MacIcon name="export" size={14} />
              </button>
            </div>
          </div>
        </section>
        {section(
          2,
          t("Préparez les inscriptions et le planning"),
          t(
            "Créez vos groupes, visualisez et ajustez votre planning et prévoyez le déroulement de votre compétition dans le module de planification.",
          ),
          [
            t("Gérez les inscriptions et les informations des participants."),
            t(
              "Constituez les groupes et répartissez-les sur les espaces de compétition.",
            ),
            t(
              "Planifiez les épreuves et les étapes de préparation propres à votre discipline.",
            ),
          ],
          "planning",
        )}
        {section(
          3,
          t("Gérez et suivez la compétition"),
          t(
            "Retrouvez les participants, les performances et les résultats dans une interface adaptée à votre discipline.",
          ),
          [
            t(
              "Suivez les informations et les contrôles prévus par votre règlement.",
            ),
            t("Saisissez les performances et les décisions des officiels."),
            t("Automatisez l’arbitrage via la saisie mobile des arbitres."),
            t(
              "Consultez et exportez les résultats dans le format de votre choix.",
            ),
          ],
          "group",
        )}
        <section
          id="diffusion"
          className={`wrap journey-section journey-stop journey-last ${currentStep >= 4 ? "is-current" : ""}`}
          data-step="05"
        >
          <div className="journey-copy">
            <h2>{t("Diffusez en direct")}</h2>
            <p>
              {t(
                "Utilisez les affichages préparés pour les écrans de la salle et les overlays de votre diffusion vidéo.",
              )}
            </p>
            <ul>
              <li>
                {t(
                  "Affichez les résultats en direct sur le web comme sur des écrans en salle.",
                )}
              </li>
              <li>{t("Intégrez vos widgets à votre stream.")}</li>
              <li>{t("Publiez les classements pour le public.")}</li>
            </ul>
            <Link style={actionStyle("neutral")} className="btn" to="/app">
              {t("Consulter les compétitions")}
              <MacIcon name="arrow" size={14} />
            </Link>
          </div>
          <div className="live-preview">
            <div className="product-chrome">
              <strong>{t("Aperçu de diffusion")}</strong>
              <span className="product-context">{t("Démonstration")}</span>
            </div>
            <div className="live-athlete">
              <p className="eyebrow">{t("Plateau 1 · Soulevé de terre")}</p>
              <h3>Alex Martin</h3>
              <p className="quiet">{t("France · -83 kg · Essai 02")}</p>
              <div className="charge">
                185 <small>kg</small>
              </div>
              <div className="attempt">
                <span>{t("Essai réussi")}</span>
                <span
                  className="lights"
                  aria-label={t("Trois décisions favorables")}
                >
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            </div>
            <div className="live-footer">
              {t("Un affichage à vos couleurs, connecté à la compétition.")}
            </div>
          </div>
        </section>
        <section className="wrap clients-section">
          <h2>{t("Nos clients")}</h2>
          <div className="client-list">
            {[
              ["occitanie.png", "FFForce Occitanie"],
              ["hdf.png", t("Ligue de Force Hauts-de-France")],
              ["sud.png", t("Ligue Sud de Force")],
              ["kdks.svg", t("KDKS, Association Suisse de Force Athlétique")],
            ].map(([file, name]) => (
              <div key={file}>
                <img
                  src={`${assets}clients/${file}`}
                  alt={name}
                  loading="lazy"
                  width="220"
                  height="95"
                />
              </div>
            ))}
          </div>
        </section>
        <section className="simple-band" id="services">
          <div className="wrap">
            <h2>{t("Services")}</h2>
            <div className="services">
              {[
                [
                  t("Support technique"),
                  t(
                    "Un accompagnement en français pour utiliser l’application et préparer vos compétitions.",
                  ),
                ],
                [
                  t("Formation"),
                  t(
                    "Un guide d’utilisation et des formations pour les équipes d’organisation.",
                  ),
                ],
                [
                  t("Sauvegarde"),
                  t(
                    "Des sauvegardes de vos compétitions accessibles depuis votre espace en ligne.",
                  ),
                ],
                [
                  t("Mises à jour"),
                  t(
                    "Des améliorations régulières pour faire évoluer vos outils.",
                  ),
                ],
                [
                  t("Personnalisation"),
                  t(
                    "Fonctionnalités, exports et affichages : étudions les adaptations dont vous avez besoin.",
                  ),
                ],
              ].map(([title, body], i) => (
                <div key={title}>
                  <span className="eyebrow">0{i + 1}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="wrap faq">
          <h2>{t("Questions fréquentes")}</h2>
          {[
            [
              t("Faut-il un compte pour consulter les résultats ?"),
              t(
                "Les compétitions publiées en accès public peuvent être consultées sans compte. Les outils d’organisation et les espaces réservés nécessitent une connexion.",
              ),
            ],
            [
              t("Les résultats sont-ils consultables sur téléphone ?"),
              t(
                "Oui. L’application web permet de consulter les compétitions, les essais et les classements depuis un téléphone, une tablette ou un ordinateur.",
              ),
            ],
            [
              t("Comment sont mis à jour les résultats ?"),
              t(
                "Ils sont transmis depuis la compétition par l’équipe d’organisation. Leur disponibilité dépend de la publication et de la connexion de l’événement.",
              ),
            ],
            [
              t("La vidéo est-elle disponible pour toutes les compétitions ?"),
              t(
                "Une vidéo est accessible lorsqu’un lien de diffusion a été renseigné par l’organisation pour le plateau concerné.",
              ),
            ],
          ].map(([q, a]) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </section>
        <section className="wrap cta-band">
          <div>
            <h2>{t("Contact")}</h2>
            <p>
              {t("Pour une démonstration ou une question sur l’application.")}
            </p>
          </div>
          <Link style={actionStyle()} className="btn primary" to="/contact">
            {t("Nous contacter")}
            <MacIcon name="arrow" size={14} />
          </Link>
        </section>
      </main>
      <footer className="footer">
        <div className="wrap">
          <div className="footer-top">
            <Link className="brand" to="/">
              <img src={assets + "icon.png"} alt="" width="28" height="28" />
              Meet Manager
            </Link>
            <nav aria-label={t("Pied de page")}>
              <Link to="/app">{t("Compétitions")}</Link>
              <Link to="/contact">{t("Nous contacter")}</Link>
              <Link to="/licensed">{t("Mon espace")}</Link>
            </nav>
          </div>
          <div className="footer-bottom">
            <span>
              © {new Date().getFullYear()} Meet Manager · 9of9 Solutions
            </span>
            <nav aria-label={t("Informations légales")}>
              <Link to="/terms">{t("Conditions d’utilisation")}</Link>
              <Link to="/privacy">{t("Confidentialité")}</Link>
              <Link to="/legal">{t("Mentions légales")}</Link>
              <Link to="/cookies">{t("Cookies")}</Link>
            </nav>
          </div>
        </div>
      </footer>
      <dialog
        className="capture-dialog"
        ref={dialog}
        aria-labelledby="capture-title"
        onClick={(e) => {
          if (e.target === dialog.current) {
            const r = dialog.current.getBoundingClientRect();
            if (
              e.clientX < r.left ||
              e.clientX > r.right ||
              e.clientY < r.top ||
              e.clientY > r.bottom
            )
              close();
          }
        }}
      >
        <div className="dialog-heading">
          <h2 id="capture-title">
            {enlarged ? shotTitles[enlarged] : t("Capture de l’application")}
          </h2>
          <button
            className="btn"
            style={actionStyle("neutral", false)}
            onClick={close}
            aria-label={t("Fermer la capture")}
          >
            <MacIcon name="close" size={16} />
          </button>
        </div>
        {enlarged && <img src={source(enlarged)} alt={shotTitles[enlarged]} />}
        <p className="quiet">
          {t("Interface de démonstration · Données fictives")}
        </p>
      </dialog>
    </div>
  );
}
